import { useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { brl, fmtD, fmtDT, ORDER_STATUS } from "@/lib/api";
import { ConfirmActivationDialog, ConfirmPaymentDialog, NoteDialog } from "@/components/OrderDialogs";

const OPEN = ["novo_pedido", "aguardando_pagamento", "cliente_informou_pagamento", "pagamento_confirmado", "aguardando_ativacao"];
const MODALITY = { familia: "Família", "família": "Família", adulto: "Adulto (+18)" };

const Badge = ({ status, testId }) => {
  const m = ORDER_STATUS[status] || { label: status, cls: "badge-muted" };
  return <span className={`mono-badge ${m.cls}`} data-testid={testId}>{m.label}</span>;
};

const Row = ({ k, v, testId }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-[#6B7280] font-mono">{k}</p><p className="text-sm break-all" data-testid={testId}>{v || "—"}</p></div>
);

const paymentStatus = (o) => o.paid_at ? `Confirmado por ${o.payment_confirmed_by} em ${fmtDT(o.paid_at)}`
  : o.customer_reported_at ? `Cliente informou em ${fmtDT(o.customer_reported_at)} — PENDENTE DE CONFERÊNCIA` : "Não confirmado";
const activationStatus = (o) => o.activated_at ? `Ativada por ${o.activated_by} em ${fmtDT(o.activated_at)}` : o.paid_at ? "Aguardando ativação manual" : "—";

export const OrderItem = ({ o, onDone }) => {
  const [open, setOpen] = useState(false);
  const [dlg, setDlg] = useState(null);
  const close = () => setDlg(null);
  const done = () => { setDlg(null); onDone(); };
  const canPay = !o.paid_at && [...OPEN.slice(0, 3), "intervencao_manual"].includes(o.status);
  const canActivate = o.paid_at && !o.activated_at && ["aguardando_ativacao", "intervencao_manual"].includes(o.status);

  return (
    <li className="log-item" data-testid={`order-item-${o.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold">{o.id}</span>
          <Badge status={o.status} testId={`order-status-${o.id}`} />
          <span className={`mono-badge ${o.mode === "real" ? "badge-ai" : "badge-muted"}`} data-testid={`order-mode-${o.id}`}>{o.mode === "real" ? "WhatsApp real" : "SIMULADO"}</span>
        </div>
        <span className="font-heading text-lg font-bold text-[#34D399]">{brl(o.amount_cents)}</span>
      </div>
      {o.manual_reason && <p className="text-sm text-red-300 mt-2" data-testid={`order-manual-reason-${o.id}`}>⚠ {o.manual_reason}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <Row k="Cliente" v={o.sender} testId={`order-sender-${o.id}`} />
        <Row k="Conversa" v={`${o.conversation_id}${o.phone ? ` · ${o.phone}` : " · sem número"}`} />
        <Row k="Login / ID Digital+" v={`${o.digital_user || "—"} / ${o.digital_id || "—"}${o.digital_user && !o.digital_verified ? " (não verificado)" : ""}`} testId={`order-digital-${o.id}`} />
        <Row k="Plano" v={`${o.screens} tela(s) · ${o.period} (${o.months}m)${o.modality ? ` · ${MODALITY[o.modality] || o.modality}` : ""}`} />
        <Row k="Pedido em" v={fmtDT(o.created_at)} />
        <Row k="Pagamento" v={paymentStatus(o)} testId={`order-payment-${o.id}`} />
        <Row k="Ativação" v={activationStatus(o)} testId={`order-activation-${o.id}`} />
        <Row k="Vencimento (admin)" v={fmtD(o.expires_at)} testId={`order-expiry-${o.id}`} />
      </div>
      {o.mp_link && <a href={o.mp_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#22D3EE] mt-2 font-mono" data-testid={`order-link-${o.id}`}>{o.mp_link}<ExternalLink className="w-3 h-3" /></a>}
      <div className="flex flex-wrap gap-2 mt-3">
        {canPay && <button className="chip chip-active" onClick={() => setDlg("pay")} data-testid={`order-confirm-payment-${o.id}`}>Confirmar pagamento</button>}
        {canActivate && <button className="chip chip-active" onClick={() => setDlg("act")} data-testid={`order-confirm-activation-${o.id}`}>Confirmar ativação</button>}
        {OPEN.includes(o.status) && <button className="chip" onClick={() => setDlg("manual")} data-testid={`order-manual-${o.id}`}>Intervenção manual</button>}
        {[...OPEN, "intervencao_manual"].includes(o.status) && <button className="chip hover:text-red-400" onClick={() => setDlg("cancel")} data-testid={`order-cancel-${o.id}`}>Cancelar</button>}
      </div>
      <button className="text-xs text-[#6B7280] mt-3 flex items-center gap-1" onClick={() => setOpen(!open)} data-testid={`order-toggle-history-${o.id}`}>{open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}Histórico ({o.history.length})</button>
      {open && <div className="mt-2 space-y-1 text-xs font-mono text-[#9CA3AF]" data-testid={`order-history-${o.id}`}>{o.history.map((h, i) => <p key={i}>{fmtDT(h.at)} · {h.status} · {h.actor || "sistema"} · {h.note}</p>)}</div>}
      {dlg === "pay" && <ConfirmPaymentDialog o={o} open onClose={close} onDone={done} />}
      {dlg === "act" && <ConfirmActivationDialog o={o} open onClose={close} onDone={done} />}
      {(dlg === "manual" || dlg === "cancel") && <NoteDialog o={o} open kind={dlg} onClose={close} onDone={done} />}
    </li>
  );
};
